import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { StoryService } from '../../services/story.service';
import { PaginatedDataSource } from '../../model/pagination/paginated-data-source.model';
import { Story } from '../../model/story/story.model';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { StoryStatus } from '../../model/story/story-status.enum';
import { MatIcon } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { Neighbourhood } from '../../model/story/neighbourhood.model';
import { combineLatest, debounceTime, finalize, Subscription } from 'rxjs';
import { ThemeService } from '../../services/theme.service';
import { StoryTheme } from '../../model/story/story-theme.model';
import { TagService } from '../../services/tag.service';
import { StoryTag } from '../../model/story/story-tag.model';
import { MatInput } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { RouterLink } from '@angular/router';
import { NeighbourhoodService } from '../../services/neighbourhood.service';
import { MatDialog } from '@angular/material/dialog';
import { CsvFileImportedDialog } from './csv-file-imported-dialog/csv-file-imported-dialog';
import { GenericErrorDialog } from '../generic-error-dialog/generic-error-dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DeleteStoryDialog } from '../../create-edit-story/delete-story-dialog/delete-story-dialog';

@Component({
  selector: 'app-story-list',
  imports: [
    MatCard,
    MatTableModule,
    MatPaginator,
    AsyncPipe,
    MatProgressBar,
    MatChipSet,
    MatChip,
    MatIcon,
    DatePipe,
    MatFormFieldModule,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatInput,
    MatDatepickerModule,
    MatButton,
    MatIconButton,
    MatCheckbox,
    RouterLink,
    MatTooltip,
  ],
  templateUrl: './story-list.html',
  styleUrl: './story-list.scss',
})
export class StoryList implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  // Services
  private storyService = inject(StoryService);
  private neighbourhoodService = inject(NeighbourhoodService);
  private themeService = inject(ThemeService);
  private tagService = inject(TagService);
  private readonly matDialog = inject(MatDialog);
  private readonly matSnackBar = inject(MatSnackBar);
  // Pagination
  public dataSource!: PaginatedDataSource<Story>;
  public displayedColumns: string[] = ['id', 'title', 'author', 'area', 'thema', 'tags', 'isProject', 'status', 'actions'];
  public StoryStatus = StoryStatus;
  private pageIndex = 1;
  private pageSize = 10;
  // Filters
  // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Neighbourhood filter
  public neighbourhoodSelect = new FormControl<Neighbourhood[] | null>([]);
  public neighbourhoodList: Neighbourhood[] = [];
  private selectedAreaIdList: string[] = [];
  // Theme filter
  public storyThemeSelect = new FormControl<StoryTheme[] | null>([]);
  public storyThemeList: StoryTheme[] = [];
  private selectedThemeIdList: string[] = [];
  // Tag filter
  public tagSelect = new FormControl<StoryTag[] | null>([]);
  public storyTagList: StoryTag[] = [];
  private selectedTagIdList: string[] = [];
  // Status filter
  public statusSelect = new FormControl<StoryStatus | string>('');
  private selectedStatus: StoryStatus | string = '';
  // Is project
  public isProjectCheckbox = new FormControl<boolean>(false);
  private isProject = false;
  public isActiveCheckbox = new FormControl<boolean>(true);
  private isActive = true;
  // Date filter
  public dateFrom = new FormControl<Date | null>(null);
  public dateUntil = new FormControl<Date | null>(null);
  private selectedDateFrom = '';
  private selectedDateUntil = '';
  // Subscription management
  private subscriptionList: Subscription[] = [];
  // Reset state
  private resetFiltersInProgress = false;
  // Local storage key for filter persistence
  private static readonly STORAGE_KEY = 'story-list-filters';

  constructor() {
    this.dataSource = new PaginatedDataSource<Story>(this.storyService);
    this.loadFiltersData();
    this.observeFilterChanges();
  }

  public ngAfterViewInit(): void {
    this.paginator?.page.subscribe((pageEvent: PageEvent) => {
      this.pageSize = pageEvent.pageSize;
      this.pageIndex = pageEvent.pageIndex + 1;
      this.loadPaginatedData();
    });

    // Reset to page 1 if current page has no results (e.g., after filter is applied)
    this.subscriptionList.push(
      this.dataSource.paginatedData$.subscribe((response) => {
        if (response.items.length === 0 && this.pageIndex > 1) {
          this.pageIndex = 1;
          this.paginator.pageIndex = 0;
          this.loadPaginatedData();
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptionList.forEach(subscription => subscription.unsubscribe());
  }

  private loadFiltersData(): void {
    // Track loading state for each filter data source
    let neighbourhoodsLoaded = false;
    let themesLoaded = false;
    let tagsLoaded = false;

    const tryRestoreFilters = (): void => {
      if (neighbourhoodsLoaded && themesLoaded && tagsLoaded) {
        this.restoreFiltersFromStorage();
      }
    };

    // Neighbourhood
    this.neighbourhoodService.getNeighbourhoodList()
      .subscribe((neighbourhoodList: Neighbourhood[]): void => {
        this.neighbourhoodList = neighbourhoodList;
        neighbourhoodsLoaded = true;
        tryRestoreFilters();
      });
    // Theme
    this.themeService.getThemeList()
      .subscribe((storyThemeList: StoryTheme[]): void => {
        this.storyThemeList = storyThemeList;
        themesLoaded = true;
        tryRestoreFilters();
      });
    // Tag
    this.tagService.getTagList()
      .subscribe((storyTagList: StoryTag[]): void => {
        this.storyTagList = storyTagList;
        tagsLoaded = true;
        tryRestoreFilters();
      });
  }

  private observeFilterChanges(): void {
    this.subscriptionList.push(
      // Search query
      this.searchQueryInput.valueChanges.pipe(debounceTime(300)).subscribe((value: string) => {
        this.searchQuery = value;
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
      // Neighbourhood
      this.neighbourhoodSelect.valueChanges.subscribe((selectedNeighbourhoodList: Neighbourhood[] | null): void => {
        if (selectedNeighbourhoodList !== null) {
          this.selectedAreaIdList = selectedNeighbourhoodList.map(neighbourhood => neighbourhood.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Theme
      this.storyThemeSelect.valueChanges.subscribe((selectedThemeIdList: StoryTheme[] | null): void => {
        if (selectedThemeIdList !== null) {
          this.selectedThemeIdList = selectedThemeIdList.map((storyTheme: StoryTheme) => storyTheme.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Tag
      this.tagSelect.valueChanges.subscribe((selectedTagList: StoryTag[] | null): void => {
        if (selectedTagList !== null) {
          this.selectedTagIdList = selectedTagList.map((tag: StoryTag) => tag.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Status
      this.statusSelect.valueChanges.subscribe((newStatus: StoryStatus | string | null): void => {
        if (newStatus !== null) {
          this.selectedStatus = newStatus;
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Date
      combineLatest([
        this.dateFrom.valueChanges,
        this.dateUntil.valueChanges,
      ]).subscribe(([dateFrom, dateUntil]: [Date | null, Date | null]): void => {
        if (dateFrom !== null) {
          // Use local date components to avoid UTC conversion issues
          this.selectedDateFrom = this.formatDateToLocalString(dateFrom);
        }
        if (dateUntil !== null) {
          // Use local date components to avoid UTC conversion issues
          this.selectedDateUntil = this.formatDateToLocalString(dateUntil);
        }
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
      // Project
      this.isProjectCheckbox.valueChanges.subscribe((isProjectCheckbox: boolean | null): void => {
        this.isProject = isProjectCheckbox ?? false;
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
      // Active
      this.isActiveCheckbox.valueChanges.subscribe((isActiveCheckbox: boolean | null): void => {
        this.isActive = isActiveCheckbox ?? false;
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
    );
  }

  /**
   * Format a date to YYYY-MM-DD string using local timezone
   * (avoids UTC conversion issues with toISOString)
   */
  private formatDateToLocalString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Save current filter state to local storage
   */
  private saveFiltersToStorage(): void {
    if (this.resetFiltersInProgress) {
      return;
    }
    const filterState = {
      searchQuery: this.searchQuery,
      selectedAreaIdList: this.selectedAreaIdList,
      selectedThemeIdList: this.selectedThemeIdList,
      selectedTagIdList: this.selectedTagIdList,
      selectedStatus: this.selectedStatus,
      selectedDateFrom: this.selectedDateFrom,
      selectedDateUntil: this.selectedDateUntil,
      isProject: this.isProject,
      isActive: this.isActive,
    };
    localStorage.setItem(StoryList.STORAGE_KEY, JSON.stringify(filterState));
  }

  /**
   * Restore filter state from local storage
   */
  private restoreFiltersFromStorage(): void {
    const stored = localStorage.getItem(StoryList.STORAGE_KEY);
    if (!stored) {
      // No stored filters, load with default state
      this.loadPaginatedData();
      return;
    }

    try {
      const filterState = JSON.parse(stored);
      this.resetFiltersInProgress = true;

      // Restore search query
      if (filterState.searchQuery) {
        this.searchQuery = filterState.searchQuery;
        this.searchQueryInput.setValue(filterState.searchQuery, { emitEvent: false });
      }

      // Restore neighbourhood filter
      if (filterState.selectedAreaIdList?.length > 0) {
        this.selectedAreaIdList = filterState.selectedAreaIdList;
        const selectedNeighbourhoods = this.neighbourhoodList.filter(
          (n: Neighbourhood) => filterState.selectedAreaIdList.includes(n.id),
        );
        this.neighbourhoodSelect.setValue(selectedNeighbourhoods, { emitEvent: false });
      }

      // Restore theme filter
      if (filterState.selectedThemeIdList?.length > 0) {
        this.selectedThemeIdList = filterState.selectedThemeIdList;
        const selectedThemes = this.storyThemeList.filter(
          (t: StoryTheme) => filterState.selectedThemeIdList.includes(t.id),
        );
        this.storyThemeSelect.setValue(selectedThemes, { emitEvent: false });
      }

      // Restore tag filter
      if (filterState.selectedTagIdList?.length > 0) {
        this.selectedTagIdList = filterState.selectedTagIdList;
        const selectedTags = this.storyTagList.filter(
          (t: StoryTag) => filterState.selectedTagIdList.includes(t.id),
        );
        this.tagSelect.setValue(selectedTags, { emitEvent: false });
      }

      // Restore status filter
      if (filterState.selectedStatus) {
        this.selectedStatus = filterState.selectedStatus;
        this.statusSelect.setValue(filterState.selectedStatus, { emitEvent: false });
      }

      // Restore date filters (append T00:00:00 to parse in local timezone instead of UTC)
      if (filterState.selectedDateFrom) {
        this.selectedDateFrom = filterState.selectedDateFrom;
        this.dateFrom.setValue(new Date(filterState.selectedDateFrom + 'T00:00:00'), { emitEvent: false });
      }
      if (filterState.selectedDateUntil) {
        this.selectedDateUntil = filterState.selectedDateUntil;
        this.dateUntil.setValue(new Date(filterState.selectedDateUntil + 'T00:00:00'), { emitEvent: false });
      }

      // Restore project checkbox
      if (filterState.isProject !== undefined) {
        this.isProject = filterState.isProject;
        this.isProjectCheckbox.setValue(filterState.isProject, { emitEvent: false });
      }

      // Restore project checkbox
      if (filterState.isActive !== undefined) {
        this.isActive = filterState.isActive;
        this.isActiveCheckbox.setValue(filterState.isActive, { emitEvent: false });
      }

      this.resetFiltersInProgress = false;
    } catch (e) {
      console.error('Failed to restore filters from storage:', e);
      localStorage.removeItem(StoryList.STORAGE_KEY);
    }

    // Load data with restored filters
    this.loadPaginatedData();
  }

  /**
   * Load paginated and filtered data from the back-end
   */
  public loadPaginatedData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      page: this.pageIndex,
      pageSize: this.pageSize,
      'areas[]': this.selectedAreaIdList,
      'themas[]': this.selectedThemeIdList,
      'tags[]': this.selectedTagIdList,
      'project': this.isProject,
      'active': this.isActive,
    };
    // Check conditional filters Status and Dates
    if (this.selectedStatus !== '') {
      params.status = this.selectedStatus;
    }
    if (this.selectedDateFrom !== '') {
      params.dateFrom = this.selectedDateFrom;
    }
    if (this.selectedDateUntil !== '') {
      params.dateTo = this.selectedDateUntil;
    }
    // Load data
    this.dataSource.loadData({
      params,
    });
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('');
    this.neighbourhoodSelect.reset([]);
    this.storyThemeSelect.reset([]);
    this.tagSelect.reset([]);
    this.statusSelect.reset('');
    this.dateFrom.reset(null);
    this.dateUntil.reset(null);
    this.isProjectCheckbox.reset(false);
    this.isActiveCheckbox.reset(true);
    this.resetFiltersInProgress = false;

    // also reset your "selected…" variables if needed
    this.searchQuery = '';
    this.selectedAreaIdList = [];
    this.selectedThemeIdList = [];
    this.selectedTagIdList = [];
    this.selectedStatus = '';
    this.selectedDateFrom = '';
    this.selectedDateUntil = '';
    this.isProject = false;
    this.isActive = true;

    // Clear stored filters
    localStorage.removeItem(StoryList.STORAGE_KEY);

    // Reset page
    this.pageIndex = 1;

    // Reload data
    this.loadPaginatedData();
  }

  /**
   * Fired when a CSV file is selected
   */
  public onFileSelected(input: HTMLInputElement): void {
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const formData = new FormData();
      formData.append('file', file);

      this.storyService.importStories(formData)
        .pipe(
          finalize((): void => {
            // Clear the file input
            input.value = '';
          }),
        )
        .subscribe({
          // Notify user that it went good
          next: () => this.matDialog.open(CsvFileImportedDialog),
          // Notify user something went wrong
          error: () => this.matDialog.open(GenericErrorDialog),
        });

    }
  }

  /**
   * Ask for confirmation and delete the story, keeping the current filters
   */
  public deleteStory(story: Story, event: MouseEvent): void {
    // Keep the click from bubbling up to the row
    event.stopPropagation();

    const dialogRef = this.matDialog.open(DeleteStoryDialog);

    dialogRef.afterClosed().subscribe((confirmed: boolean | undefined): void => {
      if (confirmed !== true) {
        return;
      }
      this.storyService.deleteStory(story.id)
        .subscribe({
          next: (): void => {
            this.matSnackBar.open('Verhaal succesvol verwijderd.', '', {duration: 3000});
            // Reload the list with the filters that are currently applied
            this.loadPaginatedData();
          },
          error: (): void => {
            this.matSnackBar.open('Een fout heeft opgetreden, probeer het nogmaals.', '', {duration: 3000});
          },
        });
    });
  }
}
